import React, { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import { Row, Table, Container, Col, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { marginsReport, updateProjectCosts } from "../service";

const statusOptions = [
  { label: "Production", value: "production" },
  { label: "Delivered", value: "delivered" },
];

const getMonthEnd = (monthStr) => {
  if (!monthStr) return null;
  const [year, month] = monthStr.split("-");
  return new Date(year, Number(month), 0);
};

const MarginDashboard = () => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [startPlacedDate, setStartPlacedDate] = useState("");
  const [endPlacedDate, setEndPlacedDate] = useState("");
  const [startDeliveredDate, setStartDeliveredDate] = useState("");
  const [endDeliveredDate, setEndDeliveredDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [projects, setProjects] = useState([]);
  const [costEditModalOpen, setCostEditModalOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [materialCostInput, setMaterialCostInput] = useState("");
  const [supportCostInput, setSupportCostInput] = useState("");
  const [isSavingCosts, setIsSavingCosts] = useState(false);
  const [editableCostFields, setEditableCostFields] = useState({
    material: false,
    support: false,
  });

  useEffect(() => {
    marginsReport()
      .then((res) => setProjects(res.projects))
      .catch(() => alert("Error fetching margins"));
  }, []);

  const formatCurrency = (val) =>
    val?.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    });

  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const calculateMargin = (revenue, cost) => revenue - cost;

  const calculateMarginPct = (revenue, cost) =>
    ((revenue - cost) / revenue) * 100;

  const filteredMargins = useMemo(() => {
    return projects.filter((item) => {
      const placedDate = item.placed_date ? new Date(item.placed_date) : null;
      const deliveredDate = item.delivered_date
        ? new Date(item.delivered_date)
        : null;

      const matchesStatus =
        !selectedStatus || item.status === selectedStatus.value;

      const matchesPlacedStart =
        !startPlacedDate ||
        (placedDate && placedDate >= new Date(startPlacedDate));

      const matchesPlacedEnd =
        !endPlacedDate ||
        (placedDate && placedDate <= getMonthEnd(endPlacedDate.slice(0, 7)));

      const matchesDeliveredStart =
        !startDeliveredDate ||
        (deliveredDate && deliveredDate >= new Date(startDeliveredDate));

      const matchesDeliveredEnd =
        !endDeliveredDate ||
        (deliveredDate &&
          deliveredDate <= getMonthEnd(endDeliveredDate.slice(0, 7)));

      const searchLower = searchTerm.toLowerCase();

      const matchesSearch =
        !searchTerm ||
        item.customer?.toLowerCase().includes(searchLower) ||
        item.project?.toLowerCase().includes(searchLower);

      return (
        matchesStatus &&
        matchesPlacedStart &&
        matchesPlacedEnd &&
        matchesDeliveredStart &&
        matchesDeliveredEnd &&
        matchesSearch
      );
    });
  }, [
    projects,
    selectedStatus,
    startPlacedDate,
    endPlacedDate,
    startDeliveredDate,
    endDeliveredDate,
    searchTerm,
  ]);

  const sortedMargins = useMemo(() => {
    if (!sortField) return filteredMargins;

    return [...filteredMargins].sort((a, b) => {
      let valA, valB;

      if (sortField === "marginPct") {
        valA = calculateMarginPct(a.revenue, a.cost);
        valB = calculateMarginPct(b.revenue, b.cost);
      } else {
        valA = a[sortField];
        valB = b[sortField];
      }

      if (sortField === "placed_date" || sortField === "delivered_date") {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      }

      if (valA == null) valA = -Infinity;
      if (valB == null) valB = -Infinity;

      if (typeof valA === "string") {
        return sortDirection === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      return sortDirection === "asc" ? valA - valB : valB - valA;
    });
  }, [filteredMargins, sortField, sortDirection]);

  const totals = useMemo(() => {
    let totalRevenue = 0;
    let totalEffRevenue = 0;
    let totalCost = 0;
    let totalMaterialCost = 0;
    let totalSupportCost = 0;
    let totalIdealMarginPct = 0;

    sortedMargins.forEach((item) => {
      totalRevenue += item.revenue || 0;
      totalEffRevenue += item.effective_revenue || 0;
      totalCost += item.cost || 0;
      totalMaterialCost += item.material_cost || 0;
      totalSupportCost += item.support_cost || 0;
      totalIdealMarginPct += item.ideal_margin_percentage || 0;
    });

    const totalMargin = totalEffRevenue - totalCost;
    const marginPct = (totalMargin / totalEffRevenue) * 100;

    return {
      revenue: totalRevenue,
      effectiveRevenue: totalEffRevenue,
      cost: totalCost,
      materialCost: totalMaterialCost,
      supportCost: totalSupportCost,
      margin: totalMargin,
      idealMarginPct: sortedMargins.length
        ? totalIdealMarginPct / sortedMargins.length
        : 0,
      marginPct,
    };
  }, [sortedMargins]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? "▲" : "▼";
  };

  const canEditMaterialCost = (status) => status === "production_planning";
  const canEditSupportCost = (status) =>
    ["partially_delivered", "delivered"].includes(status);

  const openCostEditModal = (item, targetField = null) => {
    const canEditMaterial = canEditMaterialCost(item.status);
    const canEditSupport = canEditSupportCost(item.status);

    if (!canEditMaterial && !canEditSupport) return;
    if (targetField === "material" && !canEditMaterial) return;
    if (targetField === "support" && !canEditSupport) return;

    setEditingProjectId(item.id);
    setMaterialCostInput(item.material_cost ?? "");
    setSupportCostInput(item.support_cost ?? "");
    setEditableCostFields({
      material: canEditMaterial,
      support: canEditSupport,
    });
    setCostEditModalOpen(true);
  };

  const handleSaveCostEdits = async () => {
    if (!editingProjectId) return;

    const parsedMaterialCost =
      materialCostInput === "" ? null : Number(materialCostInput);
    const parsedSupportCost =
      supportCostInput === "" ? null : Number(supportCostInput);

    if (
      editableCostFields.material &&
      parsedMaterialCost != null &&
      Number.isNaN(parsedMaterialCost)
    ) {
      alert("Please enter a valid material cost");
      return;
    }
    if (
      editableCostFields.support &&
      parsedSupportCost != null &&
      Number.isNaN(parsedSupportCost)
    ) {
      alert("Please enter a valid support cost");
      return;
    }

    setIsSavingCosts(true);
    try {
      await updateProjectCosts({
        projectId: editingProjectId,
        materialCost: editableCostFields.material ? parsedMaterialCost : undefined,
        supportCost: editableCostFields.support ? parsedSupportCost : undefined,
      });
      const res = await marginsReport();
      setProjects(res.projects || []);
      setCostEditModalOpen(false);
      setEditingProjectId(null);
      setEditableCostFields({ material: false, support: false });
    } catch (err) {
      const msg =
        err?.error ||
        err?.message ||
        (typeof err === "string" ? err : "Failed to update costs");
      alert(msg);
    } finally {
      setIsSavingCosts(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#eef2f6" }}>
      <Container className="p-4 w-100" style={{ maxWidth: "100%" }}>
        <Row>
          <Col className="mb-2">
            <h3 className="fw-bold">Margin Dashboard</h3>
          </Col>
        </Row>

        <Row className="border p-3 bg-white rounded">
          {/* FILTERS */}
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3 px-0">
            <div style={{ minWidth: 200 }}>
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                isClearable
                placeholder="Status"
              />
            </div>

            <div>
              <label className="form-label mb-0 me-2">Placed From</label>
              <Input
                type="month"
                value={startPlacedDate ? startPlacedDate.slice(0, 7) : ""}
                onChange={(e) =>
                  setStartPlacedDate(
                    e.target.value ? e.target.value + "-01" : ""
                  )
                }
                className="w-auto"
              />
            </div>

            <div>
              <label className="form-label mb-0 me-2">Placed Till</label>
              <Input
                type="month"
                value={endPlacedDate ? endPlacedDate.slice(0, 7) : ""}
                onChange={(e) =>
                  setEndPlacedDate(e.target.value ? e.target.value + "-01" : "")
                }
                className="w-auto"
              />
            </div>

            <div>
              <label className="form-label mb-0 me-2">Delivered From</label>
              <Input
                type="month"
                value={startDeliveredDate ? startDeliveredDate.slice(0, 7) : ""}
                onChange={(e) =>
                  setStartDeliveredDate(
                    e.target.value ? e.target.value + "-01" : ""
                  )
                }
                className="w-auto"
              />
            </div>

            <div>
              <label className="form-label mb-0 me-2">Delivered Till</label>
              <Input
                type="month"
                value={endDeliveredDate ? endDeliveredDate.slice(0, 7) : ""}
                onChange={(e) =>
                  setEndDeliveredDate(
                    e.target.value ? e.target.value + "-01" : ""
                  )
                }
                className="w-auto"
              />
            </div>

            <div className="ms-auto">
              <Input
                type="text"
                placeholder="Search customer/project"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-auto"
              />
            </div>
          </div>

          {/* TABLE */}
          <Table
            striped
            bordered
            hover
            size="sm"
            className="align-middle mb-0"
            style={{ fontSize: "0.9rem" }}
          >
            <thead className="bg-secondary text-white">
              <tr>
                <th
                  scope="col"
                  onClick={() => handleSort("customer")}
                  className="bg-secondary text-white pointer"
                >
                  Customer {renderSortIcon("customer")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("project")}
                  className="bg-secondary text-white pointer"
                >
                  Project {renderSortIcon("project")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("status")}
                  className="bg-secondary text-white pointer"
                >
                  Status {renderSortIcon("status")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("placed_date")}
                  className="bg-secondary text-white pointer"
                >
                  Placed {renderSortIcon("placed_date")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("delivered_date")}
                  className="bg-secondary text-white pointer"
                >
                  Delivered {renderSortIcon("delivered_date")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("revenue")}
                  className="bg-secondary text-white pointer"
                >
                  Revenue {renderSortIcon("revenue")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("price_correction_percentage")}
                  className="bg-secondary text-white pointer"
                >
                  PC % {renderSortIcon("price_correction_percentage")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("discount_percentage")}
                  className="bg-secondary text-white pointer"
                >
                  Discount % {renderSortIcon("discount_percentage")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("effective_revenue")}
                  className="bg-secondary text-white pointer"
                >
                  Eff. Revenue {renderSortIcon("effective_revenue")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("cost")}
                  className="bg-secondary text-white pointer"
                >
                  Cost {renderSortIcon("cost")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("material_cost")}
                  className="bg-secondary text-white pointer"
                >
                  Material Cost {renderSortIcon("material_cost")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("support_cost")}
                  className="bg-secondary text-white pointer"
                >
                  Support Cost {renderSortIcon("support_cost")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("margin")}
                  className="bg-secondary text-white pointer"
                >
                  Margin {renderSortIcon("margin")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("ideal_margin_percentage")}
                  className="bg-secondary text-white pointer"
                >
                  Ideal Margin % {renderSortIcon("ideal_margin_percentage")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleSort("marginPct")}
                  className="bg-secondary text-white pointer"
                >
                  Margin % {renderSortIcon("marginPct")}
                </th>
              </tr>
              <tr className="text-dark">
                {[0, 1, 2, 3, 4].map((index) => (
                  <td key={index} className="bg-DDDDDD fw-bold"></td>
                ))}
                <td className="bg-DDDDDD fw-bold">
                  {formatCurrency(totals.revenue)}
                </td>
                <td className="bg-DDDDDD fw-bold"></td>
                <td className="bg-DDDDDD fw-bold"></td>
                <td className="bg-DDDDDD fw-bold">
                  {formatCurrency(totals.effectiveRevenue)}
                </td>
                <td className="bg-DDDDDD fw-bold">
                  {formatCurrency(totals.cost)}
                </td>
                <td className="bg-DDDDDD fw-bold">
                  {formatCurrency(totals.materialCost)}
                </td>
                <td className="bg-DDDDDD fw-bold">
                  {formatCurrency(totals.supportCost)}
                </td>
                <td className="bg-DDDDDD fw-bold">
                  {formatCurrency(totals.margin)}
                </td>
                <td className="bg-DDDDDD fw-bold">
                  {totals.idealMarginPct.toFixed(2)}%
                </td>
                <td className="bg-DDDDDD fw-bold">
                  {totals.marginPct.toFixed(2)}%
                </td>
              </tr>
            </thead>
            <tbody>
              {sortedMargins.map((item) => {
                const margin = calculateMargin(item.revenue, item.cost);
                const marginPct = calculateMarginPct(item.revenue, item.cost);

                return (
                  <tr key={item.id}>
                    <td>{item.customer}</td>
                    <td>
                      <a
                        href={`/projects/${item.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-dark text-decoration-none"
                      >
                        {item.project}
                        <span className="ms-2 text-muted small">
                          #{item.order_identifier}
                        </span>
                      </a>
                    </td>
                    <td className="text-capitalize">{item.status}</td>
                    <td>{formatDate(item.placed_date)}</td>
                    <td>{formatDate(item.delivered_date)}</td>
                    <td>{formatCurrency(item.revenue)}</td>
                    <td>
                      {item.price_correction_percentage != null
                        ? `${item.price_correction_percentage}%`
                        : "-"}
                    </td>
                    <td>
                      {item.discount_percentage != null
                        ? `${item.discount_percentage}%`
                        : "-"}
                    </td>
                    <td>{formatCurrency(item.effective_revenue)}</td>
                    <td>
                      <a
                        href={`/projects/${item.id}/orders/${item.order_id}/bom`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatCurrency(item.cost)}
                      </a>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span>
                          {item.material_cost != null
                            ? formatCurrency(item.material_cost)
                            : "-"}
                        </span>
                        {canEditMaterialCost(item.status) ? (
                          <i
                            className="fa fa-pencil text-primary pointer"
                            title="Edit material cost"
                            onClick={() => openCostEditModal(item, "material")}
                          />
                        ) : (
                          <i
                            className="fa fa-lock text-muted"
                            title="Material cost editable only in production planning status"
                          />
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span>
                          {item.support_cost != null
                            ? formatCurrency(item.support_cost)
                            : "-"}
                        </span>
                        {canEditSupportCost(item.status) ? (
                          <i
                            className="fa fa-pencil text-primary pointer"
                            title="Edit support cost"
                            onClick={() => openCostEditModal(item, "support")}
                          />
                        ) : (
                          <i
                            className="fa fa-lock text-muted"
                            title="Support cost editable only in partially delivered or delivered status"
                          />
                        )}
                      </div>
                    </td>
                    <td>{formatCurrency(margin)}</td>
                    <td>
                      {item.ideal_margin_percentage != null
                        ? `${item.ideal_margin_percentage.toFixed(2)}%`
                        : "-"}
                    </td>
                    <td>{marginPct.toFixed(2)}%</td>
                  </tr>
                );
              })}
              {sortedMargins.length === 0 && (
                <tr>
                  <td colSpan="16" className="text-center">
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Row>
      </Container>
      <Modal
        isOpen={costEditModalOpen}
        toggle={() => {
          setCostEditModalOpen(false);
          setEditableCostFields({ material: false, support: false });
        }}
      >
        <ModalHeader
          toggle={() => {
            setCostEditModalOpen(false);
            setEditableCostFields({ material: false, support: false });
          }}
        >
          Edit Costs
        </ModalHeader>
        <ModalBody>
          {editableCostFields.material && (
            <div className="mb-3">
              <label className="form-label">Material Cost</label>
              <Input
                type="number"
                value={materialCostInput}
                onChange={(e) => setMaterialCostInput(e.target.value)}
                placeholder="Enter material cost"
              />
            </div>
          )}
          {editableCostFields.support && (
            <div>
              <label className="form-label">Support Cost</label>
              <Input
                type="number"
                value={supportCostInput}
                onChange={(e) => setSupportCostInput(e.target.value)}
                placeholder="Enter support cost"
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setCostEditModalOpen(false)}
            disabled={isSavingCosts}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveCostEdits}
            disabled={isSavingCosts}
          >
            {isSavingCosts ? "Saving…" : "Save"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default MarginDashboard;
